import { useContext, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import Loading from "../loading/Loading";
import { useHistory } from "react-router-dom";
import { CloseMessage } from "../../utils/send-message/send-message";
import { DigitalAssetContext } from "../../contexts/continente-credentials/DigitalAssetContext";
import { cn } from "@/utils/utils";
import { Button } from "../ui/button";
import { ModeToggle } from "../mode-toggle";

import { Icon } from "@/components/ui/Icon"

type HeaderInputProps = {
  title: string;
  subtitle?: string;

  previousStepBackXTimes?: number;
  backClickToPushPath?: string;
  showBackButton?: boolean;

  skipStep?: () => void;
  showSkipButton?: boolean;
  skipAriaLabel?: string;

  showLoading?: boolean;
  showClose?: boolean;

  isTextAlignCenter?: boolean;

  subTitleAriaHidden?: boolean;
  subTitleAriaLabel?: string;

  topLogo?: string;

  /** "left" alinha titulo/subtitulo/logo a esquerda e coloca o logo depois do botao de voltar. */
  align?: "center" | "left";
  /** Quando definido, o botao de voltar passa a icone com moldura + este texto ao lado. */
  backLabel?: string;
};

export default function Header({
  isTextAlignCenter = true,
  showLoading = false,
  showClose = false,
  showBackButton = true,
  subTitleAriaHidden = true,
  subTitleAriaLabel = "",
  skipAriaLabel = "Nesta página poderá Aderir o Cartão Continente ou associar um que já possua. Se não quiser, por favor clique aqui. Caso contrário, avance para os campos seguintes.",
  ...props
}: HeaderInputProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const digitalAssetContext = useContext(DigitalAssetContext);

  const clickBackXTimes = (numBack: number) => {
    if (numBack >= history.length) numBack = history.length - 1;

    const numBackMinus = -Math.abs(numBack);
    history.go(numBackMinus);
  };

  const historyPush = (path: string) => {
    history.push(path);
  };

  const clickSkip = () => {
    if (props.skipStep != null) {
      props.skipStep();
    }
  };

  const clickBack = (e: MouseEvent) => {
    e.preventDefault();
    if (props.backClickToPushPath) {
      historyPush(props.backClickToPushPath);
    } else if (props.previousStepBackXTimes && props.previousStepBackXTimes > 0) {
      clickBackXTimes(props.previousStepBackXTimes);
    }
  };

  const canGoBack =
    showBackButton &&
    (props.backClickToPushPath || (props.previousStepBackXTimes && props.previousStepBackXTimes > 0));

  const topLogoElement = props.topLogo && (
    <img
      className={cn(
        "w-auto",
        "self-start mt-8 h-16"
      )}
      src={props.topLogo}
    />
  );

  return (
    <>
      <Loading showLoading={showLoading} />
      {digitalAssetContext.digitalAsset.showThemeToggle && (
        <div className="absolute top-0 left-0">
          <ModeToggle />
        </div>
      )}
      <div className="w-full">
        {topLogoElement}
        <div className="flex justify-between pt-8 h-auto">
          {canGoBack ? (
            props.backLabel ? (
              <button
                type="button"
                aria-label={props.backLabel}
                onClick={clickBack}
                className="flex w-auto cursor-pointer items-center gap-6 focus:outline-0 focus-visible:ring-2 focus-visible:ring-[var(--outline-others-focus)]"
              >
                <span className="flex size-16 items-center justify-center rounded-md border border-[var(--outline-default-variant)] bg-[var(--surface-default)]">
                  <Icon name="StreamlineArrowsArrowLeft1Regular" size={"sm"} color={"primary"} />
                </span>
                <span className="font-medium text-[var(--on-surface-default)]">{props.backLabel}</span>
              </button>
            ) : (
              <Button
                variant={"link"}
                size={"link"}
                type="button"
                aria-label={t("common.back")}
                onClick={clickBack}
                className="w-auto"
              >
                <Icon name="StreamlineArrowsArrowLeft1Regular" size={"md"} />
              </Button>
            )
          ) : <div></div>}
          {showClose && digitalAssetContext.digitalAsset.showCloseButton && (
            <Button
              variant={"link"}
              size={"link"}
              aria-label="Fechar"
              type="button"
              onClick={() => {
                CloseMessage();
              }}
              className="w-auto"
            >
              <Icon name="StreamlineEssentialCloseRegular" size={"md"} />
            </Button>
          )}
        </div>
        {props.skipStep && (
          <div className="flex flex-col items-end">
            <Button
              onClick={clickSkip}
              variant={"link"}
              size={"link"}
              type="button"
              className="w-auto px-2 py-0 h-10"
              aria-label={skipAriaLabel}
            >
              {t("common.skip_message")}
            </Button>
          </div>
        )}
        <div
          className={cn(
            "w-full flex flex-col pt-0 gap-3 mt-2 items-center justify-self-center"
          )}
        >
          <h1
            aria-label={props.title}
            className={cn('text-[var(--on-surface-default)] text-2xl font-bold', isTextAlignCenter ? "text-center" : "")}
          >
            {props.title}
          </h1>
          {props.subtitle && (
            <div
              role="heading"
              aria-hidden={subTitleAriaHidden} aria-label={subTitleAriaLabel}
              className={cn('text-base text-[var(--on-surface-default)]', isTextAlignCenter ? "text-center" : "")}
              dangerouslySetInnerHTML={{ __html: props.subtitle }}
            />
          )}
        </div>
      </div>
    </>
  );
}